import React from 'react'
import { Link } from 'gatsby'

import {
  Header,
  Layout,
  SectionTitle,
  ItemTitle,
  Intro,
  NavTabs,
  NavTabsItem,
  SmallTitle,
} from '../../../../components'

import data from './data.json'

export default () => {
  return (
    <>
      <Header
        slug={data.slug + '/develop/'}
        site={data.site}
        state="develop"
        title={data.title}
      />
      <Layout
        subtitle={data.subtitle}
        slug={data.slug + '/develop/'}
        site={data.site}
        state="develop"
        title="Search, Filter and Sort"
        complete={true}
      >
        <Intro noBorder={true}>
          <p>
            Search, filter and sort are three methods of finding information by
            refining data or changing its organization.
          </p>
        </Intro>
        <NavTabs>
          <NavTabsItem anchor="search" active={true}>
            Search
          </NavTabsItem>
          <NavTabsItem anchor="filter">Filter</NavTabsItem>
          <NavTabsItem anchor="sort">Sort</NavTabsItem>
        </NavTabs>
        <SectionTitle anchor="search">Search</SectionTitle>
        <p>
          Search allows users to find information by means of keyword entry and
          match. It can be used to narrow down visible data or as a method of
          discovery that bypasses navigation.
        </p>
        <div class="c-notification--inline-warning">
          <div class="c-notification__icon">
            <i class="icon icon--exclamation"></i>
          </div>
          <div class="c-notification__content">
            While search allows users to find relevant information quickly, it
            is not a substitute for intuitive navigation.
          </div>
        </div>
        <ItemTitle>Assets for search</ItemTitle>
        <p>
          Because the search action relies on keyword entry, all search
          components are{' '}
          <Link
            className="link"
            to={'/' + data.site + '/components/text-input/develop'}
          >
            text inputs
          </Link>
          .
        </p>
        <hr />
        <ItemTitle>Types of search</ItemTitle>
        <p>Currency defines two scopes of search, global and contextual.</p>
        <p>
          A <b>global search</b>, such as a site search will match a keyword
          against an entire product. Global search is usually placed at the top
          right of a page in the header to indicate that it is not related to a
          specific page's content.
        </p>
        <p>
          A <b>contextual search</b> will match a keyword against a smaller set
          of data points. The context of the search must be made evident to the
          user both by the input's placement in a layout and accompanying text.
          For example, if a user wants to search within their transaction
          history, place the search bar near the list of transactions and set
          the placeholder text or CTA to "Search transactions".
        </p>
        <hr />
        <ItemTitle>Behaviors</ItemTitle>
        <p>
          Search inputs can exhibit any of the following behaviors to aid the
          user in finding information and to best fit the needs of an
          experience.
        </p>
        <SmallTitle>Search Triggers</SmallTitle>
        <p>
          <b>Confirmation Search</b> - This basic functionality requires the
          user to submit their keyword before their query is matched to any
          results. The confirmation CTA must be obvious to the user and to
          screen readers.
        </p>
        <p>
          <b>Instant Search</b> - This search shortcut immediately begins
          displaying results in real-time in the same layout as the search
          input. While an instant search may help a user find information
          faster, it should always be reinforced by the ability to perform a
          confirmation search. This ensures that a user will always be able to
          find all relevant information matching their keyword while maintaining
          the accessibility and usability of the Instant Search behavior.
        </p>
        <SmallTitle>Suggestions</SmallTitle>
        <p>
          <b>Predictive Inputs</b> - Confirmation searches can make use of
          predictive input behavior (also called typeahead) to make suggestions
          based on the characters a user is typing. Upon selection, the
          suggestion will populate the searchbox and the user will submit the
          search.
        </p>
        <p>
          <b>Recent Searches</b> - On focus in a search input, an overlay can
          appear that displays recently entered keywords.
        </p>
        <hr />
        <ItemTitle>Displaying Results</ItemTitle>
        <p>
          Based on the behavior built into the search input, search results can
          be displayed one of three ways:
        </p>
        <SmallTitle>Results Page</SmallTitle>
        <p>
          This is the default display method for a confirmation search. After
          submission, the user is shown a new page that displays all keyword
          matches. This may not always require navigation but will minimally
          require page reload.
        </p>
        <SmallTitle>Overlay</SmallTitle>
        <p>
          This is a display method for an instant search wherein keyword matches
          will display in an elevated container beneath the search box. Users
          can make selections from within the container. An instant search
          overlay should only display 7 - 9 search results at a time. Users
          should always have the option to confirm the search and view more
          results on a dedicated search page.
        </p>
        <SmallTitle>On-screen Data Refinement</SmallTitle>
        <p>
          This is a display method for an instant search wherein information
          already present on the screen is eliminated as a user enters their
          keyword so that only matches remain. This display method is commonly
          seen in data tables.
        </p>
        <ItemTitle>Search Best Practices</ItemTitle>
        <ul class="c-icon-list" arialabelledby="accountdetailslabel">
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              Tell the user when there are no search results
            </span>
          </li>
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              Avoid dead ends. When possible, offer the user a go-forward action
              even when there are no results
            </span>
          </li>
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              Use a wait animation to let users know the system is working as
              intended, especially when searching large data sets and results
              may take a moment
            </span>
          </li>
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              Truncate search results instead of line wrapping
            </span>
          </li>
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              Enumerate results and categorize them when possible, e.g. "19
              results for <em>user input</em>"
            </span>
          </li>
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              Highlight keyword matches in search results
            </span>
          </li>
        </ul>
        <hr />

        <SectionTitle anchor="filter">Filter</SectionTitle>
        <p>
          Filtering allows users to narrow down results from available data by
          toggling on and off predefined attributes. Unlike search, filters
          cannot be used for navigation.
        </p>

        <ItemTitle>Assets for Filtering</ItemTitle>
        <p>
          Filters are boolean attributes that, when selected, represent a
          property a user wants matched. Dependin gon the complexity of the data
          set and rigor of the filter, any of the following assets can be used.
        </p>
        <SmallTitle>For single selection:</SmallTitle>
        <p>
          When a user can only choose one option out of a list of available
          attributes, use any input that has the same behavior.
        </p>
        <ul class="c-unordered-list l-margin-bottom--xxlarge">
          <li class="c-unordered-list__item">
            <Link
              className="link"
              to={'/' + data.site + '/components/radio/develop'}
            >
              Radio
            </Link>
          </li>
          <li class="c-unordered-list__item">
            <Link
              className="link"
              to={'/' + data.site + '/components/select/develop'}
            >
              Select
            </Link>
          </li>
          <li class="c-unordered-list__item">
            <Link
              className="link"
              to={'/' + data.site + '/components/button-group/develop'}
            >
              Button group
            </Link>
          </li>
        </ul>

        <SmallTitle>For multiple selections:</SmallTitle>
        <p>
          When a user can choose multiple options out of a list of available
          attributes, use any input that has the same behavior. Multiple
          attributes are combined using a logical "OR" operator.
        </p>
        <ul class="c-unordered-list l-margin-bottom--xxlarge">
          <li class="c-unordered-list__item">
            <Link
              className="link"
              to={'/' + data.site + '/components/checkbox/develop'}
            >
              Checkbox
            </Link>
          </li>
        </ul>

        <SmallTitle>For selecting numerical ranges:</SmallTitle>
        <p>
          When a user can choose between a range of numbers, use two number
          inputs. For date ranges, you may also use a{' '}
          <Link
            className="link"
            to={'/' + data.site + '/components/date-select/develop'}
          >
            calendar picker
          </Link>{' '}
          where layouts allow.
        </p>
        <hr />

        <ItemTitle>Triggers</ItemTitle>
        <p>
          Filters can exhibit any of the following behaviors to aid the user in
          finding information and to best fit the needs of an experience.
        </p>

        <p>
          <b>Confirmation Filter</b> - A user must submit their selected
          attributes and submit. In instances where a user submission is
          necessary, a CTA should be obvious, e.g. an "Apply Filters" button.
        </p>
        <p>
          <b>Instant Filter</b> - Filters take effect in real-time as a user
          manipulates the inputs. Changing information in the layout will alert
          the user to a successful filtering action.
        </p>

        <hr />
        <ItemTitle>Displaying Results</ItemTitle>
        <p>
          Filtering never changes the organization or layout of information so
          results are always displayed in the same method as the larger data
          set. For example, filtering a data table may change the records a user
          sees, but will never change the columns or rows in the table.
        </p>
        <hr />
        <ItemTitle>Filtering Best Practices</ItemTitle>
        <ul class="c-icon-list" arialabelledby="accountdetailslabel">
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              No filters should be applied by default when first displaying
              information
            </span>
          </li>
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              At smaller breakpoints, filters may be hidden to maximize screen
              real estate. The filter icon is an effective indication that
              filtering options are available.
            </span>
          </li>
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              If filters can be hidden, you must include an on-screen indication
              of what filters have been applied to a data set. You should also
              include an option to clear the filters without re-opening the
              container.{' '}
              <Link
                className="link"
                to={'/' + data.site + '/components/chip/develop'}
              >
                Chips
              </Link>{' '}
              are a good asset to display this information.
            </span>
          </li>
        </ul>

        <hr />

        <SectionTitle anchor="sort">Sort</SectionTitle>
        <p>
          Sort manipulates the organization of repeating information such as
          lists, card collections or records in a data table. It does not change
          how much information is displayed.
        </p>

        <ItemTitle>Assets for Sorting</ItemTitle>
        <p>
          The display of sort functionality should depend on the complexity of
          the information and the rigor of the sort.
        </p>
        <SmallTitle>Sorting by a single attribute:</SmallTitle>
        <p>
          When there is only one attribute to sort by, use a default{' '}
          <Link
            className="link"
            to={'/' + data.site + '/components/button/develop'}
          >
            button
          </Link>
          , an{' '}
          <Link
            className="link"
            to={'/' + data.site + '/components/link/develop'}
          >
            icon link
          </Link>
          , or a{' '}
          <Link
            className="link"
            to={'/' + data.site + '/components/link/develop'}
          >
            text link
          </Link>{' '}
          that specifies what attribute the user is sorting by.
        </p>

        <SmallTitle>Choosing from a list of attributes:</SmallTitle>
        <p>
          When a user can choose from a list of attributes to sort by, use a{' '}
          <Link
            className="link"
            to={'/' + data.site + '/components/select/develop'}
          >
            select
          </Link>{' '}
          that lists the various available attributes.
        </p>

        <SmallTitle>Sorting data tables:</SmallTitle>
        <p>
          Include sort functionality on each column in data tables to allow for
          toggling descending/ascending sort.
        </p>
        <hr />

        <ItemTitle>Triggers</ItemTitle>
        <p>Sort is always instantly triggered when an attribute is selected.</p>

        <hr />
        <ItemTitle>Sort Best Practices</ItemTitle>
        <ul class="c-icon-list" arialabelledby="accountdetailslabel">
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              The default sort order should be obvious to the user
            </span>
          </li>
          <li class="c-icon-list__item">
            <span class="c-icon-list__icon">
              <i class="icon icon--check-mark"></i>
            </span>
            <span class="c-icon-list__content u-color--white">
              Always let the user know what attribute data is being sorted by
            </span>
          </li>
        </ul>
      </Layout>
    </>
  )
}
